const RECENT_SEARCHES_KEY = 'suds.search.recent.v1';
const MAX_RECENT_SEARCHES_PER_DOCSPACE = 10;
const MAX_RECENT_SEARCH_QUERY_LENGTH = 200;
const MAX_RECENT_SEARCH_TITLE_LENGTH = 500;
const MAX_RECENT_SEARCH_CONTENT_LENGTH = 1000;
const MAX_RECENT_SEARCH_URL_LENGTH = 1000;

interface RecentSearchItem {
  docLeaf: string;
  title: string;
  content: string;
  url: string;
}


export interface RecentSearchEntry extends RecentSearchItem {
  query: string;
  ts: number;
}

type RecentSearchBucket = Record<string, RecentSearchEntry[]>;

const isSafeRecentSearchUrl = (value: string): boolean => {
  if (process.env.SUDS_ENV === 'local' || process.env.SUDS_ENV === 'development') {
    return true;
  }

  const normalizedValue = value.trim();
  if (!normalizedValue) return false;
  if (normalizedValue.startsWith('//')) return false;
  if (typeof window === 'undefined' || !window.location) return false;

  try {
    const parsed = new URL(normalizedValue, window.location.href);
    return (
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
      parsed.origin === window.location.origin
    );
  } catch {
    return false;
  }
};

const isRecentSearchEntry = (value: unknown): value is RecentSearchEntry => {
  if (!value || typeof value !== 'object') return false;
  const entry = value as RecentSearchEntry;
  return (
    typeof entry.docLeaf === 'string' &&
    typeof entry.title === 'string' &&
    typeof entry.content === 'string' &&
    typeof entry.url === 'string' &&
    isSafeRecentSearchUrl(entry.url) &&
    typeof entry.query === 'string' &&
    typeof entry.ts === 'number'
  );
};

const trimTo = (value: string, max: number): string =>
  value.trim().slice(0, max);

const readBucket = (): RecentSearchBucket => {
  try {
    if (typeof window === 'undefined') return {};
    const raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    return Object.entries(parsed as Record<string, unknown>).reduce(
      (bucket, [docSpaceId, entries]) => {
        if (!Array.isArray(entries)) return bucket;
        bucket[docSpaceId] = entries.filter(isRecentSearchEntry);
        return bucket;
      },
      {} as RecentSearchBucket,
    );
  } catch {
    return {};
  }
};

const writeBucket = (bucket: RecentSearchBucket): void => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(bucket));
  } catch {
    // localStorage can be unavailable in private mode, quota-limited, or sandboxed.
  }
};


export const getRecentSearches = (
  docSpaceId: string | undefined,
): RecentSearchEntry[] => {
  if (!docSpaceId) return [];
  return readBucket()[docSpaceId] ?? [];
};

export const addRecentSearch = (
  docSpaceId: string | undefined,
  item: RecentSearchItem,
  query: string,
): void => {
  if (!docSpaceId || !item.url) return;

  const entry: RecentSearchEntry = {
    docLeaf: trimTo(item.docLeaf, MAX_RECENT_SEARCH_TITLE_LENGTH),
    title: trimTo(item.title, MAX_RECENT_SEARCH_TITLE_LENGTH),
    content: trimTo(item.content, MAX_RECENT_SEARCH_CONTENT_LENGTH),
    url: trimTo(item.url, MAX_RECENT_SEARCH_URL_LENGTH),
    query: trimTo(query, MAX_RECENT_SEARCH_QUERY_LENGTH),
    ts: Date.now(),
  };

  if (!entry.url || !entry.title || !isSafeRecentSearchUrl(entry.url)) {
    return;
  }

  const bucket = readBucket();
  const entries = (bucket[docSpaceId] ?? []).filter(
    (recentEntry) => recentEntry.url !== entry.url,
  );
  entries.unshift(entry);
  bucket[docSpaceId] = entries.slice(0, MAX_RECENT_SEARCHES_PER_DOCSPACE);
  writeBucket(bucket);
};


export const removeRecentSearch = (
  docSpaceId: string | undefined,
  url: string,
): void => {
  if (!docSpaceId) return;

  const bucket = readBucket();
  const entries = (bucket[docSpaceId] ?? []).filter(
    (entry) => entry.url !== url,
  );

  if (entries.length) {
    bucket[docSpaceId] = entries;
  } else {
    delete bucket[docSpaceId];
  }

  writeBucket(bucket);
};

export const clearRecentSearches = (docSpaceId: string | undefined): void => {
  if (!docSpaceId) return;

  const bucket = readBucket();
  delete bucket[docSpaceId];
  writeBucket(bucket);
};
